import { useMemo } from 'react';
import { getAgGridColId } from '../components/BlotterTable/columns/getAgGridColId';
import { useSecurityIdentifierColumns } from '../components/BlotterTable/columns/securityIdentifiers';
import type { Column, ColumnDef } from '../components/BlotterTable/columns/types';
import { useDefaultColumns } from '../components/BlotterTable/useDefaultColumns';
import { EMPTY_ARRAY } from '../utils/empty';
import { getEntitySubAccountNames, type AllocationBE } from './Allocation';
import { getFixingIndicesList, type FixingDetails } from './FixingDetails';
import {
  TradeSourceEnum,
  type ExecTypeEnum,
  type MultilegReportingTypeEnum,
  type PricingModeEnum,
  type SideEnum,
  type TradeActionEnum,
  type TradeStatusEnum,
} from './types';

// OMSTradeCapture4211
export class Trade {
  static readonly rowID = 'TradeID';
  /** @deprecated Use containers/Blotters/Trades/index.tsx DEFAULT_TRADE_COLUMNS instead */
  static readonly defaultColumns: (keyof Trade)[] = [
    'TransactTime',
    'Side',
    'Symbol',
    'Quantity',
    'Price',
    'Fee',
    'PriceAllIn',
    'Amount',
    'SubAccount',
    'TradeID',
    'MarketAccount',
    'TradeStatus',
    'OrderID',
    'EID',
    'AggressorSide',
  ];

  TradeID!: string;

  OrderID!: string;

  Symbol!: string;

  Quantity!: string;

  Market!: any;

  Amount!: string;

  Fee?: string;

  User!: string;

  Timestamp!: string;

  DealtCurrency?: string;

  MarketTradeID?: string;
  MarketOrderID?: string;

  TalosFee?: string;

  TalosFeeCurrency?: string;

  Price?: string;

  PriceAllIn?: string;

  AllocID?: string;

  MessageID?: string;

  CredentialID?: string;

  TransactTime!: string;

  ExecType?: ExecTypeEnum;

  Currency?: string;

  AmountCurrency?: string;

  FeeCurrency?: string;

  TradeStatus?: TradeStatusEnum;

  Side?: SideEnum;

  AggressorSide?: SideEnum;

  OID?: string;

  EID?: string;

  QuoteID?: string;

  RFQID?: string;

  Group?: string;

  Counterparty?: string;

  Comments?: string;

  ParentTradeID?: string;

  MultilegReportingType?: MultilegReportingTypeEnum;

  TradeSource?: TradeSourceEnum;

  TradeAction?: TradeActionEnum;

  Revision?: string;

  UpdateUser?: string;

  MarketAccount!: string;

  MarketAccountID?: string;

  SubAccount?: string;
  Allocation?: AllocationBE;
  /** An array of all the sub accounts used. For allocs, will be the alloc members. If no allocs, will just be the sub account in an array. Else undefined. */
  subAccounts: string[] | undefined;

  Attributes?: {
    TxHash?: string;
  };

  PricingReference?: string;

  ClearingAccount?: string;

  PricingMode?: PricingModeEnum;

  FixingDetails?: FixingDetails;
  fixingIndices: string[] | undefined;

  get isManualBookedTrade() {
    return this.TradeSource === TradeSourceEnum.User;
  }

  get isOrphanTrade() {
    return this.OrderID == null;
  }

  /** True if the OrderID on the Trade is actually a CustomerOrderID */
  get isOrderIDCustomerOrderID() {
    return this.Market === 'dealer';
  }

  constructor(data: Trade) {
    Object.assign(this, data);
    this.subAccounts = getEntitySubAccountNames(this);
    this.fixingIndices = getFixingIndicesList(this.FixingDetails);
  }
}

interface UseTradeColumns {
  defaultColumns?: (keyof Trade | Partial<Column>)[];
}

export function useTradeColumns({ defaultColumns = EMPTY_ARRAY }: UseTradeColumns): Column[] {
  const securityIdentifierColumns = useSecurityIdentifierColumns('Symbol');
  const defaultVisibleColumns = useMemo(
    () =>
      new Map(
        (
          [
            {
              field: 'TransactTime',
              type: 'date',
              initialSort: '-',
              initialWidth: 140,
              sortable: true,
              params: { milliseconds: true },
            },
            {
              field: 'Side',
              type: 'side',
              sortable: true,
            },
            {
              field: 'Symbol',
              type: 'security',
              sortable: true,
              params: { includeSecurityIdentifiersInQuickFilter: true },
            },
            {
              field: 'Quantity',
              type: 'size',
              title: 'Filled Quantity',
              sortable: true,
              params: { currencyField: 'Currency' },
              initialWidth: 120,
            },
            {
              field: 'Price',
              type: 'price',
              title: 'Filled Price',
              sortable: true,
              params: { assetField: 'Symbol' },
              initialWidth: 120,
            },
            {
              field: 'Fee',
              type: 'size',
              sortable: true,
              params: { currencyField: 'FeeCurrency' },
              initialWidth: 80,
            },
            {
              id: 'confirmSynopsis',
              type: 'confirmSynopsis',
              title: 'Confirm',
              initialHide: true,
            },
            {
              field: 'PriceAllIn',
              type: 'price',
              title: 'Filled Price All-In',
              sortable: true,
              params: { assetField: 'Symbol' },
            },
            {
              field: 'Amount',
              type: 'size',
              title: 'Filled Counter Amt.',
              sortable: true,
              params: { currencyField: 'AmountCurrency' },
              initialWidth: 120,
            },
            {
              field: 'MarketAccount',
              type: 'marketAccount',
              sortable: true,
              initialWidth: 100,
            },
            {
              field: 'AggressorSide',
              type: 'side',
              sortable: true,
              minWidth: 150,
            },
            {
              id: 'SubAccount',
              field: 'subAccounts',
              type: 'subAccount',
              title: 'Sub Account(s)',
              sortable: true,
            },
            {
              field: 'TradeID',
              type: 'id',
              sortable: true,
            },
            {
              field: 'TradeStatus',
              type: 'tradeStatus',
              sortable: true,
            },
            {
              field: 'OrderID',
              type: 'id',
            },
            {
              field: 'User',
              type: 'user',
              sortable: true,
            },
          ] satisfies ColumnDef<Trade>[]
        ).map(c => [getAgGridColId(c), c])
      ),
    []
  );
  const defaultHiddenColumns = useMemo(() => {
    const columns = new Map<string, Column>(
      (
        [
          {
            field: 'Market',
            type: 'market',
            sortable: true,
          },
          {
            field: 'EID',
            type: 'text',
          },
          {
            field: 'Timestamp',
            type: 'date',
            sortable: true,
            initialSort: '+',
            params: { milliseconds: true },
          },
          {
            field: 'DealtCurrency',
            type: 'text',
          },
          {
            field: 'MarketTradeID',
            type: 'text',
          },
          {
            field: 'MarketOrderID',
            type: 'text',
          },
          {
            field: 'TalosFee',
            type: 'size',
          },
          {
            field: 'TalosFeeCurrency',
            type: 'currency',
          },
          {
            field: 'AllocID',
            type: 'text',
          },
          {
            field: 'Currency',
            type: 'currency',
            sortable: true,
          },
          {
            field: 'AmountCurrency',
            type: 'currency',
          },
          {
            field: 'FeeCurrency',
            type: 'currency',
          },
          {
            field: 'OID',
            type: 'text',
          },
          {
            field: 'QuoteID',
            type: 'id',
          },
          {
            field: 'RFQID',
            type: 'id',
          },
          {
            field: 'Group',
            type: 'text',
            sortable: true,
          },
          {
            field: 'Counterparty',
            type: 'customer',
            title: 'Customer',
            description: 'Customer counterparty for this trade.',
            sortable: true,
          },
          {
            // Custom id preserves the legacy `Counterparty` colId for persisted Customer column layouts.
            id: 'CounterpartyDisplay',
            field: 'Counterparty',
            type: 'counterparty',
            title: 'Counterparty',
            description: 'Counterparty for this trade, including customers and non-customer counterparties.',
            sortable: true,
          },
          {
            field: 'Comments',
            type: 'text',
            sortable: true,
          },
          {
            field: 'ParentTradeID',
            type: 'id',
          },
          {
            field: 'MultilegReportingType',
            title: 'Multileg Reporting Type',
            type: 'text',
          },
          {
            field: 'TradeSource',
            type: 'text',
          },
          {
            field: 'TradeAction',
            type: 'text',
          },
          {
            field: 'Revision',
            type: 'text',
          },
          {
            field: 'UpdateUser',
            type: 'user',
            sortable: true,
          },
          {
            field: 'MarketAccountID',
            type: 'text',
          },
          {
            field: 'Allocation',
            type: 'text',
          },
          { field: 'Attributes.TxHash', title: 'TxHash', type: 'text', initialHide: true },
          {
            field: 'PricingReference',
            type: 'price',
            initialHide: true,
            params: {
              isReferencePrice: true,
            },
          },
          {
            field: 'ClearingAccount',
            type: 'marketAccount',
            title: 'Clearing Account',
          },
          {
            field: 'fixingIndices',
            title: 'Fixing Index',
            type: 'fixingIndices',
            id: 'fixingIndices',
          },
          {
            field: 'FixingDetails',
            title: 'Fixing Price',
            type: 'fixingPrice',
            id: 'fixingPrice',
          },
        ] satisfies (false | ColumnDef<Trade>)[]
      ).map(c => [getAgGridColId(c), { ...c, initialHide: true }])
    );
    for (const column of securityIdentifierColumns) {
      columns.set(getAgGridColId(column), { ...column, initialHide: true });
    }
    return columns;
  }, [securityIdentifierColumns]);
  const columnDefinitions = useMemo(() => {
    return new Map(
      [...defaultVisibleColumns.values(), ...defaultHiddenColumns.values()].map(c => [getAgGridColId(c), c] as const)
    );
  }, [defaultVisibleColumns, defaultHiddenColumns]);
  return useDefaultColumns(defaultColumns, columnDefinitions);
}
